import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { AbstractControl, ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';

@Component({
    selector: 'app-file-control',
    imports: [MatButtonModule, MatIconModule, ReactiveFormsModule, MatFormFieldModule, MatChipsModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FileControlComponent,
            multi: true
        }
    ],
    template: `
    <div
      class="download-container"
      [class.dragging]="isDragging"
      (dragover)="onDragOver($event)"
      (dragleave)="onDragLeave($event)"
      (drop)="handleDragDrop($event)"
      >
      <label style="color: dimgray">{{ label }}</label>
      <mat-icon color="primary">upload</mat-icon>
      <input type="file" #fileInput (change)="onFileSelected($event)" [accept]="accept" [multiple]="multiple" hidden />
      <button mat-flat-button color="primary" (click)="triggerFileInput(fileInput)">
        {{ buttonText }}
      </button>
      @if (control?.hasError('required')) {
        <mat-error>
          {{ requiredErrorMessage }}
        </mat-error>
      }
      @if (invalidFile) {
        <mat-error>
          {{ invalidFileErrorMessage }}
        </mat-error>
      }
    </div>
    
    @if (selectedFiles.length > 0) {
      <div>
        <mat-chip-listbox class="custom-chip-list">
          @for (file of selectedFiles; track file; let i = $index) {
            <mat-chip (removed)="removeFile(i)">
              {{ file.name }}
              <button matChipRemove>
                <mat-icon>cancel</mat-icon>
              </button>
            </mat-chip>
          }
        </mat-chip-listbox>
      </div>
    }
    
    @if (showSizeWarning) {
      <div class="file-size-warning">
        <p>
          La taille maximale d'un fichier ne doit pas dépasser <strong>{{ maxFileSizeMB }} MO *</strong><br />
          Seuls les fichiers au format <strong>{{ acceptLabel }}</strong> sont acceptés * <br />
          @if (recommendationText) {
            <span class="fichier-recommande">{{ recommendationText }}</span>
          }
        </p>
      </div>
    }
    `,
    styles: [
        `
      .download-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 20px;
        border: 2px dashed #ccc;
        border-radius: 5px;
        margin-bottom: 20px;
      }

      .dragging {
        background-color: rgba(0, 0, 0, 0.05);
        border-color: #3f51b5;
      }

      .custom-chip-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }

      .file-size-warning {
        margin-top: 10px;
        font-size: 0.85rem;
        color: #666;
      }

      .fichier-recommande {
        font-style: italic;
        color: #3f51b5;
      }
    `
    ]
})
export class FileControlComponent implements ControlValueAccessor {
  @Input() control?: AbstractControl;
  @Input() label = 'Glissez-déposez les fichiers ici *';
  @Input() buttonText = 'Ou cliquez ici pour sélectionner les fichiers';
  @Input() accept = 'application/pdf';
  @Input() acceptLabel = 'PDF';
  @Input() multiple = true;
  @Input() requiredErrorMessage = 'Vous devez ajouter des pièces jointes.';
  @Input() invalidFileErrorMessage = 'Seuls les fichiers PDF sont autorisés.';
  @Input() maxFileSizeMB = 300;
  @Input() showSizeWarning = true;
  @Input() recommendationText = '';

  @Output() filesSelected = new EventEmitter<File[]>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  isDragging = false;
  invalidFile = false;
  selectedFiles: File[] = [];

  // ControlValueAccessor implementation
  private onChange: any = () => {};
  private onTouched: any = () => {};

  writeValue(files: any): void {
    if (files instanceof Array) {
      this.selectedFiles = files;
    } else {
      this.selectedFiles = [];
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handle disabled state
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  handleDragDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    if (event.dataTransfer?.files) {
      this.processFiles(event.dataTransfer.files);
    }
  }

  triggerFileInput(fileInput: HTMLInputElement): void {
    fileInput.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.processFiles(input.files);
    }
  }

  removeFile(index: number): void {
    const updatedFiles = [...this.selectedFiles];
    updatedFiles.splice(index, 1);
    this.selectedFiles = updatedFiles;

    // Update form control value
    if (this.control) {
      this.control.setValue(this.selectedFiles.length > 0 ? this.selectedFiles : null);
      this.control.markAsDirty();
    }

    // Call ControlValueAccessor callbacks
    this.onChange(this.selectedFiles.length > 0 ? this.selectedFiles : null);
    this.onTouched();

    // Emit updated files
    this.filesSelected.emit(this.selectedFiles);
  }

  private processFiles(fileList: FileList): void {
    this.invalidFile = false;
    const files: File[] = Array.from(fileList);

    // Validate file types
    const invalidFiles = files.some((file) => !this.isValidFileType(file));

    if (invalidFiles) {
      this.invalidFile = true;
      return;
    }

    // Add new files to existing files if multiple is true
    if (this.multiple) {
      this.selectedFiles = [...this.selectedFiles, ...files];
    } else {
      this.selectedFiles = files;
    }

    // Update form control value
    if (this.control) {
      this.control.setValue(this.selectedFiles);
      this.control.markAsDirty();
      this.control.markAsTouched();
    }

    // Call ControlValueAccessor callbacks
    this.onChange(this.selectedFiles);
    this.onTouched();

    // Emit files for parent component
    this.filesSelected.emit(this.selectedFiles);

    // Reset file input
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  private isValidFileType(file: File): boolean {
    if (this.accept === '*') return true;

    const acceptedTypes = this.accept.split(',').map((type) => type.trim());
    return acceptedTypes.some((type) => {
      if (type.startsWith('.')) {
        // Check file extension
        return file.name.toLowerCase().endsWith(type.toLowerCase());
      } else {
        // Check MIME type
        return file.type === type || (type.endsWith('/*') && file.type.startsWith(type.replace('/*', '/')));
      }
    });
  }
}
